import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Tooltip from '@mui/material/Tooltip';

import SftGlobalData from '../library/SftGlobalData';
import Colors from '../library/Colors';
import ReportRunModal from '../components/reports/ReportRunModal';
import RevenueMapModal from '../components/reports/RevenueMapModal';
import DashboardModal from '../components/reports/DashboardModal';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { makeMatcher } from '../library/reportSearch';
import { clearLegacyFavorites, fetchFavorites, legacyFavoritesKey, readLegacyFavorites, saveFavorite } from '../library/reportFavorites';
import '../App.css';
import '../styles/main.css';

const localizedTitle = (report, lang) => {
    if (!report) return '';
    switch (lang) {
        case 'nl': return report.reptitle  || report.reptitle2 || report.reptitle3 || report.reptitle4 || '';
        case 'fr': return report.reptitle2 || report.reptitle  || report.reptitle3 || report.reptitle4 || '';
        case 'en': return report.reptitle3 || report.reptitle  || report.reptitle2 || report.reptitle4 || '';
        default:   return report.reptitle  || report.reptitle2 || report.reptitle3 || report.reptitle4 || '';
    }
};

// Plain "contains" search, or SQL LIKE wildcards (% and _) once the term
// contains a % (library/reportSearch).
const matchSearch = (report, matcher, lang) => matcher([
    localizedTitle(report, lang),
    report.reptitle,
    report.reptitle2,
    report.reptitle3,
    report.reptitle4,
    report.repmodule,
    String(report.repid),
    String(report.repreleaseid ?? ''),
]);

// Collapsed report lists ({ custom, softtouch }) survive a reload of the tab.
const COLLAPSED_KEY = 'sft_reports_collapsed';
const loadCollapsed = () => {
    try {
        const v = JSON.parse(window.localStorage.getItem(COLLAPSED_KEY) || '{}');
        return { custom: !!v && v.custom === true, softtouch: !!v && v.softtouch === true };
    } catch (e) {
        return { custom: false, softtouch: false };
    }
};

// Height model of a report list region: divider + column header + rows
// (all rows are rowHeight px, border included).
const LIST_ROW = 36;
const LIST_EMPTY = 68;          // the "no reports" box
const MIN_LIST = 140;
const MIN_LIST_BUDGET = 2 * (MIN_LIST + 1 + LIST_ROW);
const LIST_ANIM_MS = 250;
const LIST_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';   // MUI easeInOut, as Collapse uses
const listChrome = (items) => 1 + (items.length > 0 ? LIST_ROW : 0);
const listContent = (items) => (items.length > 0 ? items.length * LIST_ROW : LIST_EMPTY);

/** First guess for the list budget, corrected by measuring (see ReportsScreen). */
const estimateListBudget = () => 2 * Math.min(640, Math.max(MIN_LIST, Math.floor((window.innerHeight - 560) / 2)))
    + 2 * (1 + LIST_ROW);

/**
 * Body max-height per open list: one open list takes the whole budget; two
 * share it equally, and what a short list does not need goes to the other.
 */
const allocateLists = (budgetPx, lists) => {
    const open = lists.filter(l => l.open);
    const out = {};
    if (open.length === 0) return out;
    const avail = budgetPx - open.reduce((sum, l) => sum + listChrome(l.items), 0);
    if (open.length === 1) {
        out[open[0].id] = Math.max(Math.min(MIN_LIST, listContent(open[0].items)), avail);
        return out;
    }
    const [a, b] = open;
    const needA = listContent(a.items);
    const needB = listContent(b.items);
    const half = Math.floor(avail / 2);
    if (needA <= half) {
        out[a.id] = needA;
        out[b.id] = avail - needA;
    } else if (needB <= half) {
        out[b.id] = needB;
        out[a.id] = avail - needB;
    } else {
        out[a.id] = half;
        out[b.id] = avail - half;
    }
    out[a.id] = Math.max(Math.min(MIN_LIST, needA), out[a.id]);
    out[b.id] = Math.max(Math.min(MIN_LIST, needB), out[b.id]);
    return out;
};

/** Height the open list regions really take with this allocation. */
const usedListHeight = (alloc, lists) => lists.filter(l => l.open)
    .reduce((sum, l) => sum + listChrome(l.items) + Math.min(listContent(l.items), alloc[l.id] || 0), 0);

/** The element that scrolls the page (the app's content pane, else the document). */
const scrollerOf = (el) => {
    let n = el ? el.parentElement : null;
    while (n && n !== document.body) {
        const oy = window.getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.clientHeight > 0) return n;
        n = n.parentElement;
    }
    return document.scrollingElement || document.documentElement;
};

// "Only favourites" survives a reload of the tab.
const FAV_ONLY_KEY = 'sft_reports_fav_only';
const loadFavOnly = () => {
    try {
        return window.localStorage.getItem(FAV_ONLY_KEY) === '1';
    } catch (e) {
        return false;
    }
};

// The search query survives a trip to the layout designer and back (and a
// reload of the tab); a ?q= in the URL wins. Storage may be unavailable
// (private mode) - every access is guarded.
const SEARCH_KEY = 'sft_reports_search';
const loadSearch = () => {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('q');
        if (fromUrl !== null) return fromUrl;
        return window.sessionStorage.getItem(SEARCH_KEY) || '';
    } catch (e) {
        return '';
    }
};
const saveSearch = (value) => {
    try {
        if (value) window.sessionStorage.setItem(SEARCH_KEY, value);
        else window.sessionStorage.removeItem(SEARCH_KEY);
    } catch (e) {
        // storage unavailable - the query is simply not remembered
    }
};

const ReportsScreen = (props) => {
    const [loading, setLoading] = useState(true);
    const [softtouch, setSofttouch] = useState([]);
    const [custom, setCustom] = useState([]);
    const [searchTerm, setSearchTerm] = useState(loadSearch);
    const [selectedReport, setSelectedReport] = useState(null);
    // Shared sort across both lists. null = backend default order.
    const [sortField, setSortField] = useState(null); // 'repid' | 'repmodule' | 'repreleaseid' | 'description' | null
    const [sortDir, setSortDir]     = useState(null); // 'asc' | 'desc' | null

    // The web-report builder (and the reports saved with it) is hidden for
    // now; the Web reports card keeps the built-in revenue map + dashboard.
    // Built-in "Omzetkaart" revenue map (always present, not stored in web_report).
    const [mapOpen, setMapOpen] = useState(false);
    const [dashboardOpen, setDashboardOpen] = useState(false);
    // Pick lists for document reports (firms / stores / registers).
    const [lookups, setLookups] = useState(null);
    // Favourites: users_myreports in the client database, shared with
    // MyFashionManager (see reportFavorites). Report repids only.
    const [favorites, setFavorites] = useState([]);
    const [favOnly, setFavOnly] = useState(loadFavOnly);
    const [collapsed, setCollapsed] = useState(loadCollapsed);
    useEffect(() => {
        try {
            window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify(collapsed));
        } catch (e) {
            // storage unavailable - the lists simply open expanded next time
        }
    }, [collapsed]);
    // Heights only animate right after a collapse/expand, never while the
    // window is resized or the list is filtered.
    const [animating, setAnimating] = useState(false);
    const animTimer = useRef(0);
    useEffect(() => () => clearTimeout(animTimer.current), []);
    const toggleCollapsed = (id) => {
        clearTimeout(animTimer.current);
        setAnimating(true);
        animTimer.current = setTimeout(() => setAnimating(false), LIST_ANIM_MS + 100);
        setCollapsed(c => ({ ...c, [id]: !c[id] }));
    };
    // A closed list keeps the height it had, so its rows do not jump while it folds away.
    const lastAlloc = useRef({});
    useEffect(() => {
        if (!props.api) return undefined; // right after a refresh the key is not restored yet
        let cancelled = false;
        const legacyKey = legacyFavoritesKey(props.db?.db_id, props.userLogin);
        fetchFavorites(props.api).then(async (list) => {
            if (cancelled || list === null) return;
            setFavorites(list);
            // One-time move of the favourites earlier builds kept in this browser.
            const legacy = readLegacyFavorites(legacyKey).filter(id => list.indexOf(id) === -1);
            let moved = true;
            for (const id of legacy) {
                // eslint-disable-next-line no-await-in-loop
                if (await saveFavorite(props.api, id, true)) {
                    if (!cancelled) setFavorites(cur => (cur.indexOf(id) === -1 ? cur.concat([id]) : cur));
                } else {
                    moved = false;
                }
            }
            if (moved) clearLegacyFavorites(legacyKey);
        });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api]);
    useEffect(() => {
        try {
            window.localStorage.setItem(FAV_ONLY_KEY, favOnly ? '1' : '0');
        } catch (e) {
            // storage unavailable - the toggle is not remembered
        }
    }, [favOnly]);
    const isFavorite = (report) => favorites.indexOf(Number(report.repid)) !== -1;
    const flipFavorite = (report) => {
        const id = Number(report.repid);
        const on = !isFavorite(report);
        const apply = (state) => setFavorites(cur => (state
            ? (cur.indexOf(id) === -1 ? cur.concat([id]) : cur)
            : cur.filter(x => x !== id)));
        apply(on);
        saveFavorite(props.api, id, on).then(ok => {
            if (ok) return;
            apply(!on);
            props.notify(props.t('errorOccured'), 7500, 'top-center', props.theme, 1);
        });
    };

    const isDark = props.theme === 'dark';
    const accentColor = isDark ? Colors.dark_text : Colors.light_text;

    useEffect(() => {
        props.changeScreen && props.changeScreen('reports');
        // Right after a refresh (F5) the key is still empty for a moment; fetching then
        // fails and showed reports_fetch_error although the real fetch follows right after.
        if (!props.api) return undefined;
        let cancelled = false;
        SftGlobalData.post('reports&action=fetchReports', { key: props.api }).then(response => {
            if (cancelled) return;
            if (typeof response?.error === 'undefined' && response?.data) {
                setSofttouch(Array.isArray(response.data.softtouch) ? response.data.softtouch : []);
                setCustom(Array.isArray(response.data.custom) ? response.data.custom : []);
                setLookups(response.data.lookups || null);
            } else {
                props.notify(props.t('reports_fetch_error') || props.t('errorOccured'), 7500, 'top-center', props.theme, 1);
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api]);

    useEffect(() => { saveSearch(searchTerm); }, [searchTerm]);


    // Back from the layout designer (?open=repid): reopen that report.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const open = searchParams.get('open');
        if (!open || loading) return;
        const report = [...custom, ...softtouch].find(r => String(r.repid) === String(open));
        if (report) setSelectedReport(report);
        setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, searchParams]);

    const sortValueFor = (report, field, lang) => {
        switch (field) {
            case 'repid':         return Number(report.repid) || 0;
            case 'repreleaseid':  return Number(report.repreleaseid) || 0;
            case 'repmodule':     return String(report.repmodule || '').toLowerCase();
            case 'description':   return String(localizedTitle(report, lang) || '').toLowerCase();
            default:              return null;
        }
    };

    const applySort = (rows) => {
        if (!sortField || !sortDir) return rows;
        const copy = [...rows];
        copy.sort((a, b) => {
            const av = sortValueFor(a, sortField, props.lang);
            const bv = sortValueFor(b, sortField, props.lang);
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ?  1 : -1;
            return 0;
        });
        return copy;
    };

    const matcher = useMemo(() => makeMatcher(searchTerm), [searchTerm]);
    const visible = (r) => matchSearch(r, matcher, props.lang) && (!favOnly || isFavorite(r));
    const filteredSofttouch = useMemo(
        () => applySort(softtouch.filter(visible)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [softtouch, matcher, props.lang, sortField, sortDir, favOnly, favorites]
    );
    const filteredCustom = useMemo(
        () => applySort(custom.filter(visible)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [custom, matcher, props.lang, sortField, sortDir, favOnly, favorites]
    );

    // Click header: cycle null → asc → desc → null. Switching to a new column starts at asc.
    const toggleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDir('asc');
        } else if (sortDir === 'asc') {
            setSortDir('desc');
        } else {
            setSortField(null);
            setSortDir(null);
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return null;
        return sortDir === 'asc'
            ? <ArrowUpwardIcon fontSize="inherit" sx={{ fontSize: '0.95rem', ml: 0.5 }} />
            : <ArrowDownwardIcon fontSize="inherit" sx={{ fontSize: '0.95rem', ml: 0.5 }} />;
    };

    const headerCellSx = (field) => ({
        cursor: field ? 'pointer' : 'default',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        '&:hover': field ? { color: 'text.primary' } : undefined,
    });

    const rowHeight = LIST_ROW;
    const gridTemplate = '28px 32px 70px 110px 110px minmax(0, 1fr) auto';

    const renderHeader = () => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                columnGap: 1.5,
                alignItems: 'center',
                height: rowHeight,
                px: 2,
                fontWeight: 600,
                fontSize: '0.78rem',
                textTransform: 'uppercase',
                color: 'text.secondary',
                letterSpacing: 0.4,
                backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            }}
        >
            <Box />
            <Box />
            <Box sx={headerCellSx('repid')}        onClick={() => toggleSort('repid')}>
                {props.t('id') || 'ID'}<SortIcon field="repid" />
            </Box>
            <Box sx={headerCellSx('repmodule')}    onClick={() => toggleSort('repmodule')}>
                {props.t('module') || 'Module'}<SortIcon field="repmodule" />
            </Box>
            <Box sx={headerCellSx('repreleaseid')} onClick={() => toggleSort('repreleaseid')}>
                {props.t('reports_repid') || 'Report ID'}<SortIcon field="repreleaseid" />
            </Box>
            <Box sx={headerCellSx('description')}  onClick={() => toggleSort('description')}>
                {props.t('reports_description') || 'Description'}<SortIcon field="description" />
            </Box>
            <Box />
        </Box>
    );

    const rowDividerColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

    const favTip = (on) => (on
        ? ((props.t('reports_favorite_remove') && props.t('reports_favorite_remove') !== 'reports_favorite_remove') ? props.t('reports_favorite_remove') : 'Remove from favourites')
        : ((props.t('reports_favorite_add') && props.t('reports_favorite_add') !== 'reports_favorite_add') ? props.t('reports_favorite_add') : 'Add to favourites'));

    const renderRow = (report, idx, isLast) => (
        <ListItem
            key={'r' + report.repid}
            disablePadding
            sx={{
                height: rowHeight,
                borderBottom: isLast ? 'none' : `1px solid ${rowDividerColor}`,
            }}
        >
            <ListItemButton
                onClick={() => setSelectedReport(report)}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    columnGap: 1.5,
                    alignItems: 'center',
                    height: rowHeight,
                    minHeight: rowHeight,
                    px: 2,
                    py: 0,
                }}
            >
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); flipFavorite(report); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label={favTip(isFavorite(report))}
                    title={favTip(isFavorite(report))}
                    sx={{ p: 0.25, color: isFavorite(report) ? '#f5b301' : 'text.disabled' }}
                >
                    {isFavorite(report) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {report.is_layout_only && (
                        <Tooltip title={props.t('reports_layoutOnlyWarning') || 'This report uses a layout file and might not be shown fully correctly.'}>
                            <ArticleOutlinedIcon fontSize="small" sx={{ color: Colors.sft_blue }} />
                        </Tooltip>
                    )}
                </Box>
                <Typography variant="body2" noWrap color="text.secondary">{report.repid}</Typography>
                <Typography variant="body2" noWrap>{report.repmodule || '—'}</Typography>
                <Typography variant="body2" noWrap>{report.repreleaseid ?? '—'}</Typography>
                <Typography variant="body2" noWrap title={localizedTitle(report, props.lang)}>
                    {localizedTitle(report, props.lang)}
                </Typography>
                {report.has_recid && !report.has_variables ? (
                    <Chip
                        size="small"
                        icon={<ArticleOutlinedIcon />}
                        label={(props.t('reports_document') && props.t('reports_document') !== 'reports_document') ? props.t('reports_document') : 'Document'}
                        variant="outlined"
                        sx={{ height: 24 }}
                    />
                ) : report.has_variables ? (
                    <Chip
                        size="small"
                        icon={<TuneIcon />}
                        label={report.has_required_var
                            ? (props.t('reports_variables_required') || 'Required')
                            : (props.t('reports_variables_optional') || 'Variables')
                        }
                        color={report.has_required_var ? 'warning' : 'default'}
                        variant="outlined"
                        sx={{ height: 24 }}
                    />
                ) : <span />}
            </ListItemButton>
        </ListItem>
    );

    const renderList = (id, title, items) => {
        const open = !collapsed[id];
        if (open && alloc[id] !== undefined) lastAlloc.current[id] = alloc[id];
        const bodyHeight = open && alloc[id] !== undefined
            ? alloc[id]
            : (lastAlloc.current[id] !== undefined ? lastAlloc.current[id] : MIN_LIST);
        const toggleTip = open
            ? ((props.t('reports_collapse') && props.t('reports_collapse') !== 'reports_collapse') ? props.t('reports_collapse') : 'Collapse')
            : ((props.t('reports_expand') && props.t('reports_expand') !== 'reports_expand') ? props.t('reports_expand') : 'Expand');
        return (
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }} className={props.theme}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 0, '&:last-child': { pb: 0 } }}>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}
                        title={toggleTip}
                        onClick={() => toggleCollapsed(id)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleCollapsed(id);
                            }
                        }}
                        sx={{
                            px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
                            cursor: 'pointer', userSelect: 'none',
                            '&:focus-visible': { outline: `2px solid ${Colors.sft_blue}`, outlineOffset: -2 },
                        }}
                    >
                        <ExpandMoreIcon sx={{
                            color: accentColor,
                            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: `transform ${LIST_ANIM_MS}ms ${LIST_EASING}`,
                        }} />
                        <Typography variant="h6" sx={{ color: accentColor, flex: 1 }}>{title}</Typography>
                        <Chip label={items.length} size="small" />
                    </Box>
                    <Collapse in={open} timeout={LIST_ANIM_MS} easing={LIST_EASING}>
                        <Divider />
                        {items.length > 0 && renderHeader()}
                        <Box sx={{
                            maxHeight: bodyHeight + 'px',
                            overflowY: 'auto',
                            transition: animating ? `max-height ${LIST_ANIM_MS}ms ${LIST_EASING}` : 'none',
                        }}>
                            {items.length === 0 && (
                                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                    <Typography variant="body2">{props.t('reports_noResults') || 'No reports'}</Typography>
                                </Box>
                            )}
                            {items.length > 0 && (
                                <List dense disablePadding>
                                    {items.map((report, idx) => renderRow(report, idx, idx === items.length - 1))}
                                </List>
                            )}
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>
        );
    };

    // Fit the open report lists to the window so the page itself never
    // scrolls (only the lists do). `budget` is the height all list regions
    // (divider + column header + rows) may take together; it does not depend
    // on which list is collapsed, so collapsing or expanding only re-divides
    // it (and animates) without measuring. It is measured when the page
    // loads, the window resizes or a list's length changes: start from an
    // estimate and correct by the overflow of the page's scroll container -
    // in layout effects, so before the browser paints (no flicker).
    const pageRef = useRef(null);
    const [budget, setBudget] = useState(null);
    const [fitTick, setFitTick] = useState(0);
    const fitKey = useRef('');
    const fitPasses = useRef(0);
    useEffect(() => {
        const onResize = () => setFitTick(t => t + 1);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const lists = [
        { id: 'custom', items: filteredCustom, open: !collapsed.custom },
        { id: 'softtouch', items: filteredSofttouch, open: !collapsed.softtouch },
    ];
    const alloc = allocateLists(budget === null ? estimateListBudget() : budget, lists);
    useLayoutEffect(() => {
        if (loading) return;
        const key = fitTick + '|' + filteredCustom.length + '|' + filteredSofttouch.length;
        if (key !== fitKey.current) {
            fitKey.current = key;
            fitPasses.current = 3;
            const estimate = estimateListBudget();
            if (estimate !== budget) {
                setBudget(estimate);
                return;
            }
        }
        if (fitPasses.current <= 0) return;
        fitPasses.current -= 1;
        const sc = scrollerOf(pageRef.current);
        const overflow = sc.scrollHeight - sc.clientHeight;
        const current = budget === null ? estimateListBudget() : budget;
        const next = Math.max(MIN_LIST_BUDGET, usedListHeight(alloc, lists) - overflow - 1);
        if (overflow > 0 && next < current) {
            setBudget(next);
        } else {
            fitPasses.current = 0;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, fitTick, budget, filteredCustom.length, filteredSofttouch.length]);

    const outerClass = (props.screenWidth < 600 ? 'not_expanded' : 'expanded')
        + ' container_content _80perc '
        + props.theme + ' container_content_' + props.theme;

    return (
        <div ref={pageRef} className={outerClass} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
                <Typography variant="h5" sx={{ color: accentColor }}>{props.t('reports_title') || 'Reports'}</Typography>
                {/* ml auto: search + favourites toggle pushed to the right edge, aligned with the cards below. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
                <TextField
                    size="small"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Escape') setSearchTerm(''); }}
                    placeholder={props.t('reports_search') || 'Search reports…'}
                    sx={{ flex: { xs: 1, sm: 'none' }, width: { xs: 'auto', sm: 260 }, '& .MuiInputBase-input': { py: 0.75, fontSize: 14 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton size="small" edge="end" onClick={() => setSearchTerm('')}
                                    aria-label={(props.t('reports_search_clear') && props.t('reports_search_clear') !== 'reports_search_clear') ? props.t('reports_search_clear') : 'Clear search'}
                                    title={(props.t('reports_search_clear') && props.t('reports_search_clear') !== 'reports_search_clear') ? props.t('reports_search_clear') : 'Clear search'}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    className={props.theme}
                />
                <Tooltip title={favOnly
                    ? ((props.t('reports_favorites_showAll') && props.t('reports_favorites_showAll') !== 'reports_favorites_showAll') ? props.t('reports_favorites_showAll') : 'Show all reports')
                    : ((props.t('reports_favorites_only') && props.t('reports_favorites_only') !== 'reports_favorites_only') ? props.t('reports_favorites_only') : 'Show favourites only')}>
                    <IconButton
                        size="small"
                        onClick={() => setFavOnly(v => !v)}
                        aria-pressed={favOnly}
                        aria-label={(props.t('reports_favorites_only') && props.t('reports_favorites_only') !== 'reports_favorites_only') ? props.t('reports_favorites_only') : 'Show favourites only'}
                        sx={{
                            border: '1px solid',
                            borderColor: favOnly ? '#f5b301' : (isDark ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'),
                            borderRadius: 1,
                            p: '5px',
                            color: favOnly ? '#f5b301' : 'text.secondary',
                        }}
                    >
                        {favOnly ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                </Box>
            </Stack>

            {/* Web reports: built-in revenue map + dashboard (builder hidden) */}
            <Card sx={{ width: '100%' }} className={props.theme}>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                        <Typography variant="h6" sx={{ color: accentColor }}>
                            {props.t('webreport_savedHeading') || 'Web reports'}
                        </Typography>
                    </Box>
                    <List dense disablePadding sx={{ pb: 1 }}>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => setMapOpen(true)} sx={{ px: 2 }}>
                                <MapOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: Colors.sft_blue }} />
                                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                                    {props.t('webreport_map_title') || 'Revenue map (Belgium)'}
                                </Typography>
                                <Chip size="small" variant="outlined" label={props.t('webreport_map_badge') || 'Map'} sx={{ mr: 4 }} />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => setDashboardOpen(true)} sx={{ px: 2 }}>
                                <DashboardOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: Colors.sft_blue }} />
                                <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                                    {(props.t('webreport_dashboard_title') && props.t('webreport_dashboard_title') !== 'webreport_dashboard_title') ? props.t('webreport_dashboard_title') : 'Dashboard'}
                                </Typography>
                                <Chip size="small" variant="outlined" label={(props.t('webreport_dashboard_badge') && props.t('webreport_dashboard_badge') !== 'webreport_dashboard_badge') ? props.t('webreport_dashboard_badge') : 'Dashboard'} sx={{ mr: 4 }} />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </CardContent>
            </Card>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && (
                <Stack direction="column" spacing={2}>
                    {renderList('custom',    props.t('reports_customHeading')    || 'Custom reports',    filteredCustom)}
                    {renderList('softtouch', props.t('reports_softtouchHeading') || 'SoftTouch reports', filteredSofttouch)}
                </Stack>
            )}

            <ReportRunModal
                open={selectedReport !== null}
                report={selectedReport}
                onClose={() => setSelectedReport(null)}
                api={props.api}
                sftUserId={props.sftUserId}
                isSft={props.isSft === true}
                lookups={lookups}
                t={props.t}
                lang={props.lang}
                theme={props.theme}
                screenWidth={props.screenWidth}
                notify={props.notify}
            />


            <DashboardModal
                open={dashboardOpen}
                onClose={() => setDashboardOpen(false)}
                api={props.api}
                t={props.t}
            />

            <RevenueMapModal
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                api={props.api}
                sftUserId={props.sftUserId}
                t={props.t}
                lang={props.lang}
                theme={props.theme}
                screenWidth={props.screenWidth}
                notify={props.notify}
            />
        </div>
    );
};

export default ReportsScreen;
